import React, { useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Typography, IconButton, Slider } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import InfoSection from '../components/InfoSection';

const News = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        setIsPlaying(false);
      });
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => setCurrentTime(video.currentTime);
    const updateDuration = () => setDuration(video.duration);

    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', updateDuration);

    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', updateDuration);
    };
  }, []);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleSliderChange = (event, newValue) => {
    if (videoRef.current) {
      videoRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <Helmet>
        <title>NGF - National Gauntlet Federation | Home</title>
        <meta
          name="description"
          content="Welcome to the National Gauntlet Federation. A new era of competitive grappling - High Energy, Fast Pace, Submission Focused, Team Driven. Tournament starts Spring 2027."
        />
        <meta
          name="keywords"
          content="NGF, National Gauntlet Federation, grappling, competitive grappling, submission grappling, BJJ, wrestling, martial arts, combat sports, tournament"
        />
        <meta property="og:title" content="NGF - National Gauntlet Federation" />
        <meta
          property="og:description"
          content="A new era of competitive grappling. High Energy. Fast Pace. Submission Focused. Team Driven."
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://ngfederation.com/" />
      </Helmet>

      {/* Custom Jumbotron for News Page */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: { xs: '70vh', sm: '80vh', md: '90vh' },
          overflow: 'hidden',
          backgroundColor: '#000',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 8, md: 10 },
        }}
      >
        {/* Background Image */}
        <Box
          component="img"
          src="public/BJJ_Main.jpg"
          alt="NGF Grappling Competition"
          loading="eager"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'brightness(0.3)',
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {/* Header Text - Above Video */}
          <Box
            sx={{
              textAlign: 'center',
              animation: 'fadeInDown 1s ease-out',
              '@keyframes fadeInDown': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(-30px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: 'white',
                textShadow: '0 4px 12px rgba(0,0,0,0.9)',
                fontSize: { xs: '2.5rem', sm: '4rem', md: '5rem' },
                mb: 2,
              }}
            >
              NATIONAL GAUNTLET FEDERATION
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: 'secondary.main',
                textShadow: '0 2px 8px rgba(0,0,0,0.9)',
                fontFamily: '"Oswald", sans-serif',
                fontWeight: 600,
                fontSize: { xs: '1.2rem', sm: '1.8rem', md: '2rem' },
                letterSpacing: '0.15em',
              }}
            >
              A NEW ERA OF COMPETITIVE GRAPPLING
            </Typography>
          </Box>

          {/* Video Container */}
          <Box
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
            sx={{
              position: 'relative',
              width: { xs: '95%', sm: '75%', md: '40%' },
              maxWidth: '1000px',
              animation: 'fadeInUp 1s ease-out 0.3s both',
              '@keyframes fadeInUp': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(30px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            <Box
              component="video"
              ref={videoRef}
              muted={isMuted}
              loop
              playsInline
              sx={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
              }}
            >
              <source
                src="https://cdn.send-atlas.com/user_304wUERayWBr8KpxBXYKNvfk2EJ/b59d1b0e-863d-47de-a13d-4284f6072e7a/NGF_Intro_2.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </Box>

            {/* Modern Video Controls - Show on Hover */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 50%, transparent 100%)',
                padding: { xs: 2, md: 3 },
                opacity: showControls ? 1 : 0,
                transition: 'opacity 0.3s ease',
                pointerEvents: showControls ? 'auto' : 'none',
              }}
            >
              {/* Progress Slider */}
              <Box sx={{ mb: 2 }}>
                <Slider
                  value={currentTime}
                  max={duration || 100}
                  onChange={handleSliderChange}
                  sx={{
                    color: 'secondary.main',
                    height: 6,
                    '& .MuiSlider-thumb': {
                      width: 16,
                      height: 16,
                      transition: 'width 0.3s, height 0.3s',
                      '&:hover': {
                        width: 20,
                        height: 20,
                      },
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    },
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 0.5,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'white', fontSize: '0.75rem' }}>
                    {formatTime(currentTime)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'white', fontSize: '0.75rem' }}>
                    {formatTime(duration)}
                  </Typography>
                </Box>
              </Box>

              {/* Control Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconButton
                  onClick={handlePlayPause}
                  sx={{
                    backgroundColor: 'rgba(13, 71, 161, 0.9)',
                    color: 'white',
                    width: { xs: 48, md: 56 },
                    height: { xs: 48, md: 56 },
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'secondary.main',
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  {isPlaying ? (
                    <PauseIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
                  ) : (
                    <PlayArrowIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
                  )}
                </IconButton>
                <IconButton
                  onClick={handleMuteToggle}
                  sx={{
                    backgroundColor: 'rgba(13, 71, 161, 0.9)',
                    color: 'white',
                    width: { xs: 48, md: 56 },
                    height: { xs: 48, md: 56 },
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'secondary.main',
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  {isMuted ? (
                    <VolumeOffIcon sx={{ fontSize: { xs: 22, md: 26 } }} />
                  ) : (
                    <VolumeUpIcon sx={{ fontSize: { xs: 22, md: 26 } }} />
                  )}
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default News;